import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_FILE_COUNT } from '../constants/fileUpload';
import { EN_STRINGS } from '../i18n/en';
import type { WidgetStrings } from '../i18n/types';
import { formatWidgetString } from './i18n';

export interface FileValidationError {
  file: File;
  reason: string;
}

export interface FileValidationOptions {
  allowedFileTypes?: string[];
  maxFileSize?: number;
  maxFileCount?: number;
  strings?: Pick<
    WidgetStrings,
    'attachmentTypeError' | 'attachmentSizeError' | 'attachmentCountError'
  >;
}

export function validateFile(file: File, options?: FileValidationOptions): string | null {
  const { allowedFileTypes, maxFileSize, strings = EN_STRINGS } = options || {};
  const allowedTypes = allowedFileTypes?.length ? new Set(allowedFileTypes) : ALLOWED_FILE_TYPES;
  const maxSize = typeof maxFileSize === 'number' && maxFileSize > 0 ? maxFileSize : MAX_FILE_SIZE;
  if (!allowedTypes.has(file.type)) {
    return formatWidgetString(strings.attachmentTypeError, {
      name: file.name,
      type: file.type || 'unknown',
      types: Array.from(allowedTypes).join(', '),
    });
  }
  if (file.size > maxSize) {
    return formatWidgetString(strings.attachmentSizeError, {
      name: file.name,
      limit: maxSize < 1024 * 1024 ? `${maxSize} B` : `${maxSize / (1024 * 1024)} MB`,
    });
  }
  return null;
}

export function validateFiles(
  files: File[],
  existingCount = 0,
  options?: FileValidationOptions
): { valid: File[]; errors: FileValidationError[] } {
  const errors: FileValidationError[] = [];
  const valid: File[] = [];

  const { maxFileCount, strings = EN_STRINGS } = options || {};
  const maxCount =
    typeof maxFileCount === 'number' && maxFileCount > 0 ? maxFileCount : MAX_FILE_COUNT;
  const totalAllowed = maxCount - existingCount;
  files.forEach(file => {
    const error =
      valid.length >= totalAllowed
        ? formatWidgetString(strings.attachmentCountError, { name: file.name, count: maxCount })
        : validateFile(file, options);
    if (error) {
      errors.push({ file, reason: error });
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}

/**
 * Validates that a URL is a safe image URL (http or https only).
 * Rejects javascript:, data:, and other potentially dangerous schemes.
 */
export function isValidImageUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}
